import { useQuery } from '@tanstack/react-query'
import React, { useState, useEffect, useRef } from 'react'
import CustomHeader from '../../components/CustomHeader'
import PAGE_TITLES from '../../consts/titles'
import Box from '@mui/material/Box'
import SkipNavigation from '../../components/SkipNavigation'
import Loader from '../../components/Loader'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import TermHeader from '../../components/TermHeader'
import { DistributionManager, TermsManager, UserManager } from '../../APIManager'
import { AdminViewerCardListFeature } from '../../features/AdminViewerDashboardFeature/AdminViewerDashboardFeature'
import CustomTooltip from '../../components/CustomTooltip'
import { ButtonSize, ButtonType } from '../../components/GenericButton/styles'
import GenericButton from '../../components/GenericButton'
import { ReactComponent as ExportIcon } from '../../assets/Export-icon.svg'
import { Typography } from '@mui/material'
import { grey } from '../../colors'
import { useUserProfile } from '../../hooks/useUserProfile'
import { notifyError } from '../../utils/notifyError'
import { type IPagination } from '../../models/DataObject'
import CustomPagination from '../../components/CustomTable/CustomPagination'

/**
 * Cards per page. Matches the instructor and student dashboards.
 *
 * Read the spec before changing it: this is the one dashboard where page size does *not* bound
 * the cost of a request. Per-card cost scales with the division's subtree — roughly 28 statements
 * plus 4.6 per section beneath the card — so a page of high-level division cards can be slow at
 * any page size. If a real tenant's page 1 is unacceptable, this constant is the only lever we
 * control, and lowering it is the response rather than accepting a timeout.
 */
export const ADMIN_CARDS_PAGE_SIZE = 20

export const AdminViewerDashboard: React.FC = (): JSX.Element => {
  const [adminName, setAdminName] = useState('')
  const [searchParams] = useSearchParams()
  const { id } = useParams()
  const termId = searchParams.get('termId')
  const { data: userProfile } = useUserProfile()
  const user = userProfile?.user

  // Identifies *which* list is on screen. The route is `/admin/:id?` and React keeps this
  // component mounted across param changes, so anything remembered about the list — the page
  // number, the last pagination — has to be keyed on both parts or it leaks from one subject's
  // list into another's. Keying on the term alone is the hole PR #1891 found on the instructor
  // dashboard.
  const listScope = `${id ?? 'self'}:${termId ?? ''}`

  // The page number is stored alongside the list it belongs to and the effective value derived,
  // rather than reset from an effect. An effect runs after render, so a term change would fire one
  // query for "page 3 of the new term" before the reset landed — a wasted request and a flash of
  // the wrong page. Same pattern as the instructor dashboard (CR-292), which learned it the hard
  // way in review.
  const [pageState, setPageState] = useState<{ scope: string, pageNumber: number }>({
    scope: listScope,
    pageNumber: 1
  })
  const pageNumber = pageState.scope === listScope ? pageState.pageNumber : 1
  const goToPage = (page: number): void => { setPageState({ scope: listScope, pageNumber: page }) }

  const { data: terms = [], isFetching: termsLoading, isError: isTermsError, error: termsError } = useQuery({
    queryKey: ['terms', user?.userId],
    staleTime: 1000,
    queryFn: async () => (await TermsManager.getAllTerms({ pageSize: 200, user_id: user?.userId })).data,
    retry: false,
    enabled: Boolean(user?.userId),
    // Same reason as the distributions query below.
    meta: { silent: true }
  })

  const { data: distributionsPage, isFetching: instructorDistributionsLoading, isError: isDistributionsError, error: distributionsError } = useQuery({
    // Page number is part of the key so pages cache independently. Note the card's own
    // invalidation uses the key prefix (`['getAdminDistributions']`,
    // AdminViewerDashboardCard.tsx), which correctly clears every cached page — do not narrow it
    // to an exact-key match.
    queryKey: ['getAdminDistributions', id, termId, pageNumber],
    queryFn: async () => {
      const params = { termId, userId: user.userId, pageNumber, pageSize: ADMIN_CARDS_PAGE_SIZE }
      if (user.userId) {
        return await DistributionManager.getAdminDistributions(params)
      }
    },
    enabled: !!termId && !!user.userId && !id,
    retry: false,
    // This query has its own `notifyError` below, and the global subscriber in ReactQueryProvider
    // does not de-duplicate against callers -- without opting out, one failure raises two toasts.
    // Paging turns that from a once-per-visit annoyance into once-per-page-change.
    meta: { silent: true }
  })

  const { data: facultyData, isFetching: facultyLoading, isError: isFacultyError, error: facultyError } = useQuery({
    queryKey: ['faculty', id, termId],
    queryFn: async () => {
      if (!id) return null
      const params = { termId }
      return (await UserManager.getFacultyAndStaffByIdApi(id, params)).data
    },
    enabled: !!id,
    // Same reason as the distributions query above.
    meta: { silent: true }
  })

  useEffect(() => {
    if (isTermsError) notifyError(termsError, { fallbackMessage: 'Failed to load terms. Please try again.' })
  }, [isTermsError])

  useEffect(() => {
    if (isDistributionsError) notifyError(distributionsError, { fallbackMessage: 'Failed to load surveys. Please try again.' })
  }, [isDistributionsError])

  useEffect(() => {
    if (isFacultyError) notifyError(facultyError, { fallbackMessage: 'Failed to load instructor data. Please try again.' })
  }, [isFacultyError])

  useEffect(() => {
    if (facultyData) {
      setAdminName(`${facultyData.firstName} ${facultyData.lastName}`)
    }
  }, [facultyData])

  useEffect(() => {
    if (!id && user) {
      setAdminName(user?.name ?? 'Courses')
    }
  }, [user, id])

  // Read straight off the query. This used to be copied into local `surveys` state by an effect
  // that mirrored it verbatim, which under pagination costs an extra render and leaves the
  // previous page on screen for it.
  const surveys = distributionsPage?.data ?? []
  const pagination = distributionsPage?.pagination

  // React Query drops `data` on error, so a failed page would otherwise unmount the pager and
  // strand the admin on the page that failed with no in-page way back to one that works. Hold the
  // last pagination read successfully. Assigning during render is safe: this is a pure
  // carry-forward of a derived value, not a state update.
  //
  // Scoped by `listScope` for the same reason the page number is — see its definition above.
  const carriedPagination = useRef<{ scope: string, pagination: IPagination } | undefined>(undefined)
  if (pagination != null) carriedPagination.current = { scope: listScope, pagination }
  const effectivePagination = pagination ??
    (carriedPagination.current?.scope === listScope ? carriedPagination.current.pagination : undefined)

  const totalResults = effectivePagination?.totalResults ?? surveys.length

  // Only the two queries that decide what the page *is* blank it. The card list's own fetch does
  // not: it now happens on every page change, and taking the whole page — term selector included
  // — away each time would be a worse experience than the list area showing a spinner, which is
  // what `isLoading` on the feature below does.
  if (facultyLoading || termsLoading) {
    return <Loader open />
  }

  const contactUsTooltip = (
    <Box display="flex" flexDirection="column" gap={1}>
      <Typography fontSize="12px">
        Export functionality is currently unavailable. Please contact support for assistance.
      </Typography>
      <Link style={{ color: grey[0], textDecoration: 'underline' }} to="mailto:support@classranked.com">
        support@classranked.com
      </Link>
    </Box>
  )

  return (
    <Box>
      <SkipNavigation />
      <CustomHeader info={adminName || PAGE_TITLES.ADMIN} />
      <AdminViewerCardListFeature
        list={surveys}
        isLoading={instructorDistributionsLoading}
        isError={isDistributionsError}
        pageNumber={pageNumber}
        // The size the server actually applied, not the size we asked for: it drives the announced
        // "Survey N of M" offset, and the pager below already reads it from here. If the backend
        // ever clamps `page_size`, the constant would announce the wrong numbers past page 1.
        pageSize={effectivePagination?.pageSize ?? ADMIN_CARDS_PAGE_SIZE}
        totalResults={totalResults}
        onBackToFirstPage={() => { goToPage(1) }}
        name={adminName}
        controls={
          <TermHeader showSeries={false} terms={terms} termsLoading={termsLoading}>

            <CustomTooltip title={contactUsTooltip}>
              <GenericButton disabled fontWeight='bold' buttonType={ButtonType.Secondary} endIcon={<ExportIcon style={{ opacity: '0.4' }} />} label='Export' onClick={() => { }} buttonSize={ButtonSize.Medium} />
            </CustomTooltip>
          </TermHeader>
        }
      />
      {/* Only when there is more than one page: an admin with a handful of cards should see the
          list exactly as they do today. */}
      {effectivePagination != null && effectivePagination.numPages > 1 && (
        <Box maxWidth={720} width='100%' margin='auto'>
          <CustomPagination
            numPages={effectivePagination.numPages}
            pageNumber={pageNumber}
            pageSize={effectivePagination.pageSize}
            totalResults={effectivePagination.totalResults}
            handlePageChange={goToPage}
            showPageSizeOptions={false}
          />
        </Box>
      )}
    </Box>
  )
}
